/**
 * Toasts, which exist here almost entirely to carry undo.
 *
 * Trashing a transaction and recategorizing one are both easy to do by accident
 * and both reversible, so they get undo rather than a confirmation dialog.
 * Confirmation is reserved for the things undo cannot reach.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import './ui.css';

interface Toast {
  id: number;
  message: string;
  tone: 'default' | 'danger';
  action?: { label: string; run: () => void };
}

interface ToastContextValue {
  show: (toast: Omit<Toast, 'id'> | string) => void;
  /** Convenience for the common case: a confirmation with an undo button. */
  showUndo: (message: string, undo: () => void) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Long enough to notice and reach for undo, short enough not to linger. */
const TOAST_DURATION_MS = 7000;

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (input: Omit<Toast, 'id'> | string) => {
      const toast: Toast =
        typeof input === 'string'
          ? { id: nextId++, message: input, tone: 'default' }
          : { ...input, id: nextId++ };
      setToasts((current) => [...current, toast]);
      window.setTimeout(() => dismiss(toast.id), TOAST_DURATION_MS);
    },
    [dismiss]
  );

  const showUndo = useCallback(
    (message: string, undo: () => void) => {
      show({ message, tone: 'default', action: { label: 'Undo', run: undo } });
    },
    [show]
  );

  const value = useMemo(() => ({ show, showUndo }), [show, showUndo]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
       * `role="status"` with polite live semantics: a confirmation should be
       * announced without interrupting whatever the user is doing.
       */}
      <div className="toast-region" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast${toast.tone === 'danger' ? ' toast--danger' : ''}`}>
            <span className="toast__message">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                className="toast__action"
                onClick={() => {
                  toast.action?.run();
                  dismiss(toast.id);
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
}
